/**
 * Dicionário em português do Brasil (idioma padrão) e fonte das chaves de tradução.
 *
 * - Chaves planas `modulo.chave`.
 * - `{nome}` marca um parâmetro, obrigatório em `t(chave, { nome })`.
 * - Mensagem com singular/plural: `{ um, outros }`, escolhida por `quantidade` (1 = `um`).
 * - `en.ts` precisa ter exatamente as mesmas chaves e formas: o compilador rejeita diferenças.
 */
export const ptBR = {
    // --- Global
    'global.carregando': 'Carregando…',
    'global.fechar': 'Fechar',
    'global.cancelar': 'Cancelar',
    'global.confirmar': 'Confirmar',
    'global.sim': 'Sim',
    'global.nao': 'Não',
    // --- Seletor de idioma
    'idioma.grupo': 'Idioma',
    'idioma.opcaoPt': 'Português (Brasil)',
    'idioma.opcaoEn': 'Inglês',
    // --- Erros genéricos da interface (categorias conhecidas do cliente HTTP)
    'erro.falhaDeRede': 'Não foi possível alcançar a Geo-Engine. Verifique o endereço configurado e o CORS.',
    'erro.status401': 'Token ausente ou inválido. Informe um token válido para continuar.',
    'erro.status404': 'Recurso não encontrado na Geo-Engine.',
    'erro.status503': 'Geo-Engine indisponível no momento.',
    'erro.respostaInesperada': 'A Geo-Engine respondeu {status} sem o contrato de erro esperado.',
    // @@FRAGMENTO:global@@
    // --- Cabeçalho e navegação
    'cabecalho.usuarioAutenticado': 'Usuário autenticado',
    'cabecalho.rotuloUsuario': 'Usuário',
    'cabecalho.menuUsuario': 'Menu do usuário',
    'cabecalho.perfil': 'Perfil',
    'cabecalho.configuracoes': 'Configurações',
    'cabecalho.sair': 'Sair',
    'navegacao.principal': 'Navegação principal',
    'navegacao.inicio': 'Início',
    'navegacao.ingestao': 'Ingestão',
    'navegacao.viewer': 'Viewer',
    'navegacao.recolherMenu': 'Recolher menu lateral',
    'navegacao.expandirMenu': 'Expandir menu lateral',
    'navegacao.recolher': 'Recolher',
    'navegacao.expandir': 'Expandir',
    // --- Home
    'home.titulo': 'Painel Operacional DFP Agro',
    'home.subtitulo': 'Gestão de dados geoespaciais, ingestões e visualização territorial.',
    'home.apresentacao': 'Apresentação institucional',
    'home.modulos': 'Módulos da plataforma',
    'home.ingestao.categoria': 'Módulo operacional',
    'home.ingestao.titulo': 'Ingestão de dados',
    'home.ingestao.descricao': 'Gerencie incorporações, acompanhe execuções e consulte camadas publicadas.',
    'home.ingestao.acao': 'Acessar ingestão',
    'home.viewer.categoria': 'Visualização territorial',
    'home.viewer.titulo': 'Viewer',
    'home.viewer.descricao': 'Explore as camadas geoespaciais disponibilizadas pela Geo-Engine.',
    'home.viewer.acao': 'Abrir Viewer',
    'home.capacidades.rotulo': 'Capacidades da plataforma',
    'home.capacidades.titulo': 'Plataforma geoespacial',
    'home.capacidades.subtitulo': 'Capacidades expostas pela Geo-Engine para o ciclo completo do dado territorial.',
    'home.capacidades.ingestao.titulo': 'Ingestão vetorial',
    'home.capacidades.ingestao.texto': 'Incorporação de arquivos geoespaciais com validação, perfil de domínio e publicação controlada.',
    'home.capacidades.ingestao.meta': 'Fontes Shapefile e GeoPackage',
    'home.capacidades.publicacao.titulo': 'Publicação de camadas',
    'home.capacidades.publicacao.texto': 'Disponibilização de dados para consumo pelas APIs da Geo-Engine, com versionamento e proveniência persistida.',
    'home.capacidades.publicacao.meta': 'APIs REST e MVT',
    'home.capacidades.exploracao.titulo': 'Exploração territorial',
    'home.capacidades.exploracao.texto': 'Visualização cartográfica das camadas publicadas, com seleção, inspeção e operações espaciais.',
    'home.capacidades.exploracao.meta': 'Mapa, atributos, geometria',
    'home.rodape': 'Plataforma DFP Agro · Geo-Engine Operational',
    // --- Login
    'login.titulo': 'Geo-Engine',
    'login.subtitulo': 'Acesso à Interface Operacional',
    'login.usuario': 'Usuário',
    'login.senha': 'Senha',
    'login.entrar': 'Entrar',
    'login.entrando': 'Entrando…',
    'login.erroCredenciais': 'Usuário ou senha inválidos.',
    'login.erroConexao': 'Não foi possível entrar. Verifique a conexão e tente novamente.',
    // --- Indicador de conexão
    'saude.inalcancavel': 'Inalcançável',
    'saude.indisponivel': 'Indisponível',
    'saude.verificando': 'Verificando',
    'saude.conexao': 'Conexão',
    'saude.conectado': 'Conectado',
    'saude.degradado': 'Degradado',
    'saude.tituloSemResposta': 'Ambiente {ambiente}. Clique para verificar novamente.',
    'saude.titulo': 'Geo-Engine {versao} · banco {banco} · ambiente {ambiente}. Clique para verificar novamente.',
    // --- Diagnóstico técnico
    'diagnostico.titulo': 'Diagnóstico técnico',
    'diagnostico.ultimaChamada': 'Última chamada',
    'diagnostico.fechar': 'Fechar diagnóstico técnico',
    'diagnostico.vazio': 'Nenhuma chamada registrada nesta sessão.',
    'diagnostico.metodo': 'Método',
    'diagnostico.endpoint': 'Endpoint',
    'diagnostico.status': 'Status',
    'diagnostico.semResposta': 'sem resposta',
    'diagnostico.duracao': 'Duração',
    'diagnostico.horario': 'Horário',
    // --- Seleção contextual de feições sobrepostas
    'seletorContextual.titulo': 'Feições neste local',
    'seletorContextual.subtitulo': 'Selecione a feição que deseja inspecionar.',
    'seletorContextual.fechar': 'Fechar seleção de feições',
    // @@FRAGMENTO:ingestao@@
    'ingestao.acao.fechar': 'Fechar',
    'ingestao.acao.cancelar': 'Cancelar',
    'ingestao.status.emExecucao': 'Em execução',
    'ingestao.status.concluida': 'Concluída',
    'ingestao.status.parcial': 'Parcial',
    'ingestao.status.falhou': 'Falhou',
    'ingestao.status.cancelada': 'Cancelada',
    'ingestao.status.pendente': 'Pendente',
    'ingestao.pagina.titulo': 'Ingestão',
    'ingestao.pagina.subtitulo': 'Submissão, acompanhamento e exclusão de execuções de ingestão.',
    'ingestao.pagina.novaIngestao': 'Nova ingestão',
    'ingestao.detalhes.titulo': 'Detalhes da execução',
    'ingestao.detalhes.carregando': 'Carregando dados da execução…',
    'ingestao.detalhes.rotuloStatus': 'Status',
    'ingestao.detalhes.rotuloInicio': 'Início',
    'ingestao.detalhes.rotuloTermino': 'Término',
    'ingestao.detalhes.rotuloLidos': 'Registros lidos',
    'ingestao.detalhes.rotuloValidos': 'Registros válidos',
    'ingestao.detalhes.rotuloRejeitados': 'Registros rejeitados',
    'ingestao.detalhes.rotuloMensagem': 'Mensagem',
    'ingestao.detalhes.rotuloEvidencias': 'Evidências de validação',
    'ingestao.detalhes.atualizando': ' · atualizando…',
    'ingestao.detalhes.acompanhamento': 'Acompanhamento em segundo plano — os dados são atualizados automaticamente.',
    'ingestao.consulta.falhaGenerica': 'Não foi possível consultar a execução.',
    'ingestao.exclusao.titulo': 'Excluir ingestão permanentemente?',
    'ingestao.exclusao.excluindo': 'Excluindo…',
    'ingestao.exclusao.confirmar': 'Excluir definitivamente',
    'ingestao.exclusao.execucaoSelecionada': 'Execução selecionada',
    'ingestao.exclusao.aviso': 'Esta operação é {irreversivel} e removerá permanentemente os registros e as publicações associados a esta execução, incluindo eventuais camadas publicadas.',
    'ingestao.exclusao.irreversivel': 'irreversível',
    'ingestao.exclusao.falhaGenerica': 'Não foi possível excluir a ingestão.',
    'ingestao.exclusao.atualizacaoFalhou': 'A exclusão da ingestão foi concluída, mas a atualização da interface falhou ({falhas}). Atualize a página para sincronizar.',
    'ingestao.exclusao.falhaHistorico': 'histórico: {detalhe}',
    'ingestao.exclusao.falhaCatalogo': 'catálogo: {detalhe}',
    'ingestao.exclusao.falhaDesconhecida': 'falha desconhecida',
    'ingestao.catalogo.regiao': 'Catálogo de camadas publicadas',
    'ingestao.catalogo.titulo': 'Camadas publicadas',
    'ingestao.catalogo.consultando': 'Consultando o catálogo…',
    'ingestao.catalogo.vazioTitulo': 'Nenhuma camada',
    'ingestao.catalogo.vazioTexto': 'Nenhuma camada operacional foi publicada ainda.',
    'ingestao.catalogo.colIdentidade': 'Identidade pública',
    'ingestao.catalogo.colNome': 'Nome',
    'ingestao.catalogo.colGeometria': 'Geometria',
    'ingestao.catalogo.colFeicoes': 'Feições',
    'ingestao.catalogo.colCrs': 'CRS de origem',
    'ingestao.execucao.regiao': 'Detalhes da execução selecionada',
    'ingestao.execucao.titulo': 'Execução selecionada',
    'ingestao.execucao.nenhumaTitulo': 'Nenhuma execução selecionada',
    'ingestao.execucao.nenhumaTexto': 'Selecione uma linha no histórico para ver os detalhes.',
    'ingestao.execucao.consultando': 'Consultando a execução…',
    'ingestao.execucao.falhaTitulo': 'Falha de consulta',
    'ingestao.execucao.acompanhando': 'Acompanhando…',
    'ingestao.historico.titulo': 'Histórico de ingestões',
    'ingestao.historico.subtitulo': 'Acompanhe as execuções e seus resultados.',
    'ingestao.historico.paginaDeTotal': 'Página {atual} de {total}',
    'ingestao.historico.consultando': 'Consultando o histórico…',
    'ingestao.historico.vazioTitulo': 'Nenhuma ingestão',
    'ingestao.historico.vazioTexto': 'Nenhuma execução de ingestão foi registrada ainda.',
    'ingestao.historico.tabela': 'Tabela de ingestões',
    'ingestao.historico.colDataset': 'Dataset',
    'ingestao.historico.colLidos': 'Lidos',
    'ingestao.historico.colValidos': 'Válidos',
    'ingestao.historico.colRejeitados': 'Rejeitados',
    'ingestao.historico.colCamada': 'Camada publicada',
    'ingestao.historico.colAcoes': 'Ações',
    'ingestao.historico.excluirIndisponivel': 'Exclusão indisponível enquanto a ingestão não termina',
    'ingestao.historico.excluirRotulo': 'Excluir a ingestão {nome}',
    'ingestao.historico.excluirAguarde': 'Aguarde a conclusão para excluir esta ingestão.',
    'ingestao.historico.excluirDica': 'Excluir definitivamente esta ingestão.',
    'ingestao.historico.excluir': 'Excluir',
    'ingestao.historico.navegacao': 'Navegação do histórico',
    'ingestao.historico.paginaAnterior': 'Página anterior',
    'ingestao.historico.registrosNestaPagina': { um: '{contagem} registro nesta página', outros: '{contagem} registros nesta página' },
    'ingestao.historico.exibindoAPartirDe': 'Exibindo a partir de {inicio}',
    'ingestao.historico.proximaPagina': 'Próxima página',
    'ingestao.formulario.erroSemArquivo': 'Selecione um arquivo .zip ou .gpkg.',
    'ingestao.formulario.erroExtensao': 'O arquivo deve ter extensão .zip ou .gpkg.',
    'ingestao.formulario.erroSemPerfil': 'Sem perfil, informe a identidade pública (public_id) e o nome da camada.',
    'ingestao.formulario.erroTerritorial': 'O perfil territorial-v1 exige o tipo territorial e o campo de nome.',
    'ingestao.formulario.erroEnvio': 'Não foi possível enviar a ingestão.',
    'ingestao.formulario.titulo': 'Nova ingestão',
    'ingestao.formulario.subtitulo': 'Configure e envie uma fonte de dados geoespaciais.',
    'ingestao.formulario.fonteTitulo': 'Fonte de dados',
    'ingestao.formulario.fonteDescricao': 'Selecione o arquivo que contém os dados geoespaciais a serem processados.',
    'ingestao.formulario.selecioneArquivo': 'Selecione um arquivo geoespacial',
    'ingestao.formulario.formatosAceitos': 'Formatos aceitos: .zip e .gpkg',
    'ingestao.formulario.substituirArquivo': 'Clique para substituir o arquivo',
    'ingestao.formulario.configTitulo': 'Configuração da ingestão',
    'ingestao.formulario.configDescricao': 'Defina o perfil de processamento e, quando necessário, a camada de origem.',
    'ingestao.formulario.perfil': 'Perfil de ingestão',
    'ingestao.formulario.semPerfil': 'Sem perfil (validação técnica genérica)',
    'ingestao.formulario.camadaDaFonte': 'Camada da fonte',
    'ingestao.formulario.camadaDaFonteAuxilio': 'Necessário apenas se o arquivo tiver mais de uma camada.',
    'ingestao.formulario.tipoTerritorial': 'Tipo territorial',
    'ingestao.formulario.campoNome': 'Campo com o nome da unidade',
    'ingestao.formulario.campoAreaOrigem': 'Campo com a área de origem (opcional)',
    'ingestao.formulario.publicacaoTitulo': 'Publicação da camada',
    'ingestao.formulario.publicacaoDescricao': 'Informações exibidas publicamente após a conclusão da ingestão.',
    'ingestao.formulario.identidade': 'Identidade pública',
    'ingestao.formulario.identidadeObrigatoria': 'Identidade pública — obrigatória sem perfil',
    'ingestao.formulario.nomePublico': 'Nome público',
    'ingestao.formulario.nomePublicoObrigatorio': 'Nome público — obrigatório sem perfil',
    'ingestao.formulario.descricao': 'Descrição (opcional)',
    'ingestao.formulario.finalidade': 'Finalidade (opcional)',
    'ingestao.formulario.atributosMvt': 'Atributos MVT publicáveis, separados por vírgula (opcional)',
    'ingestao.formulario.enviando': 'Enviando…',
    'ingestao.formulario.iniciar': 'Iniciar ingestão',
    // @@FRAGMENTO:mapa@@
    'mapa.controlesCartograficos': 'Controles cartográficos complementares',
    'mapa.desativarRelevoSombreado': 'Desativar relevo sombreado',
    'mapa.ativarRelevoSombreado': 'Ativar relevo sombreado',
    'mapa.alternarRelevoSombreado': 'Alternar relevo sombreado',
    'mapa.regiao': 'Mapa',
    'mapa.avisoSemBasemapAntes': 'Nenhum mapa-base configurado. As camadas da Geo-Engine continuam sendo desenhadas sobre um fundo neutro. Defina ',
    'mapa.avisoSemBasemapDepois': ' para usar um mapa-base.',
    'mapa.catalogoDeCamadas': 'Catálogo de camadas',
    'mapa.camadas': 'Camadas',
    'mapa.consultarCatalogoNovamente': 'Consultar o catálogo novamente',
    'mapa.consultandoCatalogo': 'Consultando o catálogo…',
    'mapa.nenhumaCamada': 'Nenhuma camada',
    'mapa.catalogoVazio': 'A Geo-Engine não publicou camadas no catálogo.',
    'mapa.ativarCamada': 'Ativar {nome}',
    'mapa.recolher': 'Recolher',
    'mapa.expandir': 'Expandir',
    'mapa.recolherCamada': 'Recolher {nome}',
    'mapa.expandirCamada': 'Expandir {nome}',
    'mapa.carregandoTiles': 'Carregando tiles',
    'mapa.falhaDeTiles': 'Falha ao carregar os tiles da camada.',
    'mapa.erro': 'erro',
    'mapa.tipoNaoSuportado': 'tipo não suportado',
    'mapa.enquadrar': 'Enquadrar',
    'mapa.ocultarConfiguracoes': 'Ocultar configurações',
    'mapa.configuracoes': 'Configurações',
    'medicao.titulo': 'Medição cartográfica',
    'medicao.medirDistancia': 'Medir distância',
    'medicao.medirArea': 'Medir área',
    'medicao.instrucaoDistancia': 'Clique no mapa para adicionar vértices. Mínimo de dois.',
    'medicao.instrucaoArea': 'Clique no mapa para adicionar vértices. Mínimo de três.',
    'medicao.vertices': 'Vértices: {quantidade}',
    'medicao.distanciaParcial': 'Distância parcial: {valor}',
    'medicao.areaParcial': 'Área parcial: {area} · Perímetro: {perimetro}',
    'medicao.erro.coordenadasInvalidas': 'Coordenadas inválidas: use longitude entre -180° e 180° e latitude entre -90° e 90°.',
    'medicao.erro.minimoDoisVertices': 'Marque ao menos dois vértices para medir a distância.',
    'medicao.erro.minimoTresVertices': 'Marque ao menos três vértices distintos para medir a área.',
    'medicao.erro.verticesRepetidos': 'O polígono possui vértices repetidos.',
    'medicao.erro.autoIntersecao': 'O polígono se cruza: refaça o desenho sem auto-interseção.',
    'medicao.erro.poligonoDegenerado': 'O polígono é degenerado: a área calculada é zero.',
    'medicao.concluir': 'Concluir',
    'medicao.cancelar': 'Cancelar',
    'medicao.limpar': 'Limpar',
    'medicao.resultadoAria': 'Resultado da medição cartográfica',
    'medicao.tituloResultado': 'Medição cartográfica — estimativa',
    'medicao.distanciaTotal': 'Distância total: {valor}',
    'medicao.area': 'Área: {valor}',
    'medicao.perimetro': 'Perímetro: {valor}',
    'medicao.nota': 'Estimativa geodésica esférica. Não constitui medida oficial ou cadastral.',
    'medicao.exportarGeoJson': 'Exportar GeoJSON',
    'relevo.titulo': 'Relevo',
    'relevo.hillshade': 'Hillshade',
    'relevo.intensidade': 'Intensidade do hillshade',
    'buffer.geometriaNaoObtida': 'A geometria da feição ainda não foi obtida da Geo-Engine.',
    'buffer.titulo': 'Distância do buffer',
    'buffer.descricao': 'Defina a distância de expansão em metros.',
    'buffer.distanciaEmMetros': 'Distância em metros',
    'buffer.auxilio': 'A Geo-Engine valida a distância informada.',
    'buffer.calcular': 'Calcular buffer',
    'buffer.limpar': 'Limpar',
    'buffer.processando': 'Processando na Geo-Engine…',
    'buffer.resultado': 'Resultado do buffer',
    'buffer.operacaoProcessada': 'Operação processada pela Geo-Engine',
    'buffer.distanciaAplicada': 'Distância aplicada',
    'buffer.valorEmMetros': '{valor} m',
    'buffer.geometria': 'Geometria',
    'legenda.titulo': 'Legenda',
    'legenda.camadasNoMapa': 'Camadas no mapa',
    'legenda.feicaoSelecionada': 'Feição selecionada',
    'legenda.resultadoDeOperacao': 'Resultado de operação',
    'legenda.hipsometrica.aria': 'Legenda de elevação do terreno',
    'legenda.hipsometrica.titulo': 'Elevação do terreno',
    'legenda.hipsometrica.subtitulo': 'Altitude em metros',
    'legenda.hipsometrica.metros': '{valor} m',
    'legenda.hipsometrica.metrosOuMais': '{valor}+ m',
    'legenda.hipsometrica.nota': 'Base hipsométrica · DEM',
    // @@FRAGMENTO:exploracao@@
    // --- Exploração (painel, catálogo, navegação)
    'exploracao.titulo': 'Exploração territorial',
    'exploracao.painel': 'Painel de exploração territorial',
    'exploracao.reabrirPainel': 'Reabrir painel',
    'exploracao.reabrirPainelNome': 'Reabrir painel de exploração territorial',
    'exploracao.catalogo': 'Catálogo',
    'exploracao.rotuloExploracao': 'Exploração',
    'exploracao.feicoesDaCamada': 'Feições da camada',
    'exploracao.detalhesDaFeicao': 'Detalhes da feição',
    'exploracao.recolherPainel': 'Recolher painel',
    'exploracao.voltarListaFeicoes': 'Voltar à lista de feições',
    'exploracao.fecharInspecao': 'Fechar inspeção',
    'exploracao.voltarCatalogoCamadas': 'Voltar ao catálogo de camadas',
    'exploracao.ativarCamada': 'Ativar {nome}',
    'exploracao.quantidadePublicadas': { um: '{quantidade} feição publicada', outros: '{quantidade} feições publicadas' },
    'exploracao.recolherCamada': 'Recolher camada',
    'exploracao.expandirCamada': 'Expandir camada',
    'exploracao.recolher': 'Recolher',
    'exploracao.expandir': 'Expandir',
    'exploracao.tilesCarregando': 'carregando tiles',
    'exploracao.tilesErro': 'erro ao carregar tiles',
    'exploracao.tilesErroComMensagem': 'erro ao carregar tiles: {mensagem}',
    'exploracao.geometriaNaoSuportada': 'geometria não suportada ({tipo})',
    'exploracao.explorarFeicoes': 'Explorar feições',
    'exploracao.enquadrarCamada': 'Enquadrar {nome}',
    'exploracao.enquadrarNoMapa': 'Enquadrar no mapa',
    'exploracao.enquadrar': 'Enquadrar',
    'exploracao.configuracoesDe': 'Configurações de {nome}',
    'exploracao.configuracoesCartograficas': 'Configurações cartográficas',
    'exploracao.contadorCamadas': { um: '{quantidade} camada publicada', outros: '{quantidade} camadas publicadas' },
    'exploracao.contadorCamadasIndisponivel': '— camadas publicadas',
    'exploracao.consultarCatalogo': 'Consultar o catálogo novamente',
    'exploracao.ordemNoMapa': 'Ordem no mapa',
    'exploracao.ordemNoMapaDescricao': 'Camadas superiores são desenhadas sobre as inferiores.',
    'exploracao.subirCamadaNome': 'Subir camada {nome}',
    'exploracao.subirCamada': 'Subir camada',
    'exploracao.descerCamadaNome': 'Descer camada {nome}',
    'exploracao.descerCamada': 'Descer camada',
    'exploracao.consultandoCatalogo': 'Consultando o catálogo…',
    'exploracao.nenhumaCamada': 'Nenhuma camada',
    'exploracao.nenhumaCamadaTexto': 'A Geo-Engine não publicou camadas no catálogo.',
    'exploracao.catalogoDeCamadas': 'Catálogo de camadas',
    'exploracao.relevoEFerramentas': 'Relevo e ferramentas',
    'exploracao.relevo': 'Relevo',
    'exploracao.relevoDescricao': 'Sombreamento e intensidade do modelo digital de elevação.',
    'exploracao.nenhumaFeicao': 'Nenhuma feição selecionada',
    'exploracao.nenhumaFeicaoTexto': 'Selecione uma feição na lista ou no mapa para inspecionar.',
    'exploracao.voltarALista': 'Voltar à lista',
    'exploracao.enquadrarFeicao': 'Enquadrar a feição no mapa',
    'exploracao.consultandoGeoEngine': 'Consultando a Geo-Engine…',
    'exploracao.camadaIndisponivel': 'Camada indisponível',
    'exploracao.camadaIndisponivelTexto': 'Esta camada não está mais disponível no catálogo da Geo-Engine.',
    'exploracao.voltarAoCatalogo': 'Voltar ao catálogo',
    'exploracao.resumoDe': 'Resumo de {nome}',
    'exploracao.rotuloCamada': 'Camada',
    'exploracao.contadorFeicoes': { um: '{quantidade} feição', outros: '{quantidade} feições' },
    'exploracao.ocultarConfiguracoes': 'Ocultar configurações',
    'exploracao.semFamiliaGeometrica': 'Camada sem família geométrica suportada para visualização.',
    // --- Feição (inspeção e apresentação)
    'feicao.selecionada': 'Feição selecionada',
    'feicao.abaDetalhes': 'Detalhes',
    'feicao.abaBuffer': 'Buffer',
    'feicao.identificador': 'Identificador',
    'feicao.geometria': 'Geometria',
    'feicao.semAtributos': 'A feição não possui atributos.',
    'feicao.rotulo': 'Feição',
    'feicao.fecharSelecionada': 'Fechar feição selecionada',
    'feicao.rotuloPadrao': 'Feição {id}',
    'feicao.valorSim': 'sim',
    'feicao.valorNao': 'não',
    // --- Exportação (CSV e GeoJSON)
    'exportacao.geojson': 'Exportar GeoJSON',
    'exportacao.csv': {
        um: 'Exportar CSV — {quantidade} carregada',
        outros: 'Exportar CSV — {quantidade} carregadas',
    },
    'exportacao.csvNota': 'Exporta apenas as feições carregadas nesta lista.',
    // --- Camada (configuração, ficha e seleção de feições)
    'camada.preenchimento': 'Preenchimento',
    'camada.corDePreenchimento': 'Cor de preenchimento',
    'camada.opacidadeDoPreenchimento': 'Opacidade do preenchimento',
    'camada.contorno': 'Contorno',
    'camada.corDoContorno': 'Cor do contorno',
    'camada.espessuraDoContorno': 'Espessura do contorno',
    'camada.opacidadeDoContorno': 'Opacidade do contorno',
    'camada.linha': 'Linha',
    'camada.ponto': 'Ponto',
    'camada.cor': 'Cor',
    'camada.opacidade': 'Opacidade',
    'camada.espessura': 'Espessura',
    'camada.tamanho': 'Tamanho',
    'camada.rotulosGeograficos': 'Rótulos geográficos',
    'camada.semAtributoNominal': 'Esta camada não possui atributo nominal publicado nos tiles.',
    'camada.exibirNomesNoMapa': 'Exibir nomes no mapa',
    'camada.atributoNominal': 'Atributo: {nome}',
    'camada.restaurarPadrao': 'Restaurar padrão',
    'camada.consultandoFicha': 'Consultando ficha…',
    'camada.erroTitulo': 'Erro',
    'camada.estatisticasIndisponiveis': 'Não foi possível obter as estatísticas da camada.',
    'camada.falhaConsultarCamada': 'Falha ao consultar a camada.',
    'camada.rotuloFeicoes': 'Feições',
    'camada.rotuloExtensao': 'Extensão',
    'camada.todos': 'Todos',
    'camada.recorteTitulo': 'Recorte espacial',
    'camada.recorteTexto': 'Consultar feições que intersectam a área visível do mapa.',
    'camada.recorteFiltrar': 'Filtrar pela área visível',
    'camada.recorteRemover': 'Remover recorte',
    'camada.recorteAtivo': 'Recorte espacial ativo',
    'camada.recorteNota': 'Área capturada no momento da aplicação.',
    'camada.recorteResultado': { um: 'Resultado: {quantidade} feição carregada neste recorte.', outros: 'Resultado: {quantidade} feições carregadas neste recorte.' },
    'camada.recorteResultadoComMais': { um: 'Resultado: {quantidade} feição carregada neste recorte. Outros resultados podem ser carregados.', outros: 'Resultado: {quantidade} feições carregadas neste recorte. Outros resultados podem ser carregados.' },
    'camada.recorteRemovido': 'Recorte removido. Consulta restaurada para toda a camada.',
    'camada.erroMapaIndisponivel': 'O mapa ainda não está disponível.',
    'camada.erroCoordenadasInvalidas': 'A área visível do mapa não possui coordenadas válidas.',
    'camada.erroLimitesGeograficos': 'A área visível ultrapassa os limites geográficos (longitude ±180°, latitude ±90°). Aproxime o mapa e tente novamente.',
    'camada.erroExtensaoInvalida': 'A área visível não define uma extensão válida (por exemplo, atravessa o antimeridiano).',
    'camada.buscarPorNome': 'Buscar feição por nome',
    'camada.buscarPlaceholder': 'Buscar nesta camada…',
    'camada.buscaIndisponivel': 'Busca textual indisponível para esta camada.',
    'camada.falhaConsultar': 'Falha ao consultar feições.',
    'camada.consultandoFeicoes': 'Consultando feições…',
    'camada.nenhumaNaArea': 'Nenhuma feição encontrada nesta área.',
    'camada.nenhumaEncontrada': 'Nenhuma feição encontrada.',
    'camada.feicoes': 'Feições',
    'camada.selecionarFeicao': 'Selecionar {nome}',
    'camada.inspecionarFeicao': 'Inspecionar {nome}',
    'camada.inspecionar': 'Inspecionar',
    'camada.falhaCarregarMaisTitulo': 'Falha ao carregar mais feições',
    'camada.falhaCarregarMais': 'Não foi possível carregar mais feições.',
    'camada.carregarMais': 'Carregar mais',
    // @@FRAGMENTOS@@
};
